// apps/vendor/src/pages/settings/BillingInfoPage.tsx
//
// Phase 1 Task 49 — uvendor billing info settings.
// PATCH /api/v1/users/me/billing-info — Tier A sensitive (requires fresh perms
// per API-CONTRACTS L3517). On 401 AUTH_FRESH_PERMS_REQUIRED / 428, FE forces
// a `/auth/refresh` and retries once.
//
// Per CONTEXT.md / API-CONTRACTS L3508: businessName, taxId, billingEmail,
// billingAddress { line1, city, postalCode, countryCode }.

import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useAuth,
  useToast,
  SettingsLayout,
} from '@printway/ui';

interface BillingForm {
  businessName: string;
  taxId: string;
  billingEmail: string;
  billingAddress: {
    line1: string;
    city: string;
    postalCode: string;
    country: string; // 2-letter ISO 3166 code (mapped to API `countryCode`)
  };
}

const INITIAL: BillingForm = {
  businessName: '',
  taxId: '',
  billingEmail: '',
  billingAddress: { line1: '', city: '', postalCode: '', country: 'VN' },
};

type LooseClient = {
  PATCH: (p: string, init?: { body?: unknown }) => Promise<{
    data?: unknown;
    error?: unknown;
    response: Response;
  }>;
};

const FRESH_PERM_ERROR_CODES = new Set([
  'AUTH_FRESH_PERMS_REQUIRED',
  'AUTH_PERM_VERSION_STALE',
]);

export function BillingInfoPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const refresh = useAuth((s) => s.refresh);
  const user = useAuth((s) => s.user);
  const fetchMe = useAuth((s) => s.fetchMe);
  const { toast } = useToast();

  const [form, setForm] = useState<BillingForm>(INITIAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // R3-10 (2026-05-16): hydrate from user.billingInfo so the form shows
  // current values on mount. Previously stayed empty → typed fields vanished
  // after save+reload → "save no-op" perception.
  useEffect(() => {
    const b = user?.billingInfo;
    if (!b) return;
    setForm({
      businessName: b.legalName ?? '',
      taxId: b.taxId ?? '',
      billingEmail: b.billingEmail ?? '',
      billingAddress: {
        line1: b.addressLine1 ?? '',
        city: b.city ?? '',
        postalCode: b.postalCode ?? '',
        country: b.country ?? 'VN',
      },
    });
  }, [user]);

  // Auto-hide success toast after 4s + don't reset on keystroke.
  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(() => setSuccess(false), 4000);
    return () => window.clearTimeout(t);
  }, [success]);

  const setField = <K extends keyof BillingForm>(
    k: K,
    v: BillingForm[K],
  ): void => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };
  const setAddr = <K extends keyof BillingForm['billingAddress']>(
    k: K,
    v: BillingForm['billingAddress'][K],
  ): void => {
    setForm((prev) => ({
      ...prev,
      billingAddress: { ...prev.billingAddress, [k]: v },
    }));
  };

  // Phase 6 alias /auth/me expects the billing block in a nested `billing`
  // key with FE-style field names (legalName / addressLine1…). The alias
  // handler remaps these onto the Phase 1 BillingInfo shape internally.
  const buildBody = (): Record<string, unknown> => {
    const billing: Record<string, unknown> = {};
    if (form.businessName) billing['legalName'] = form.businessName;
    if (form.taxId) billing['taxId'] = form.taxId;
    // R6-3 (2026-05-16): billingEmail now flows through alias.
    if (form.billingEmail) billing['billingEmail'] = form.billingEmail;
    const a = form.billingAddress as {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
    if (a.line1) billing['addressLine1'] = a.line1;
    if (a.line2) billing['addressLine2'] = a.line2;
    if (a.city) billing['city'] = a.city;
    if (a.state) billing['state'] = a.state;
    if (a.postalCode) billing['postalCode'] = a.postalCode;
    if (a.country) billing['country'] = a.country.toUpperCase();
    return { billing };
  };

  const sendPatch = async (
    body: Record<string, unknown>,
  ): Promise<Response> => {
    const client = apiClient() as unknown as LooseClient;
    const { response } = await client.PATCH(
      '/api/v1/auth/me',
      { body },
    );
    return response;
  };

  const handleSubmit = async (
    ev: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    ev.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const body = buildBody();
      if (Object.keys(body).length === 0) {
        setError(t('billing.errorEmptyForm'));
        return;
      }
      let response = await sendPatch(body);

      // Tier A — handle 401 perm-stale + retry once after /auth/refresh.
      if (response.status === 401 || response.status === 428) {
        let code = `HTTP_${response.status}`;
        try {
          const env = (await response.clone().json()) as
            | { error?: { code?: string } }
            | undefined;
          if (env?.error?.code) code = env.error.code;
        } catch {
          // ignore body parse error
        }
        if (FRESH_PERM_ERROR_CODES.has(code) || response.status === 428) {
          await refresh();
          response = await sendPatch(body);
        }
      }

      if (!response.ok) {
        let code = `HTTP_${response.status}`;
        try {
          const env = (await response.clone().json()) as
            | { error?: { code?: string } }
            | undefined;
          if (env?.error?.code) code = env.error.code;
        } catch {
          // ignore
        }
        setError(code);
        toast({
          variant: 'error',
          title: t('billing.errorTitle'),
          description: code,
        });
        return;
      }
      setSuccess(true);
      toast({
        variant: 'success',
        title: t('billing.toastSaved'),
      });
      // Refresh useAuth so user.billingInfo reflects the saved values.
      await fetchMe();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'BILLING_UPDATE_FAILED';
      setError(msg);
      toast({ variant: 'error', title: t('billing.toastUnknown'), description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsLayout portal="vendor" activeTab="billing">
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.cardTitle')}</CardTitle>
          <CardDescription>
            {t('billing.cardDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit} data-testid="billing-form">
            <FormField>
              <FormLabel htmlFor="businessName">{t('billing.businessName')}</FormLabel>
              <FormControl>
                <Input
                  id="businessName"
                  value={form.businessName}
                  onChange={(e) => setField('businessName', e.target.value)}
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="taxId">{t('billing.taxId')}</FormLabel>
              <FormControl>
                <Input
                  id="taxId"
                  value={form.taxId}
                  onChange={(e) => setField('taxId', e.target.value)}
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="billingEmail">{t('billing.billingEmail')}</FormLabel>
              <FormControl>
                <Input
                  id="billingEmail"
                  type="email"
                  value={form.billingEmail}
                  onChange={(e) => setField('billingEmail', e.target.value)}
                />
              </FormControl>
            </FormField>

            <fieldset className="mt-6 space-y-4 rounded-md border border-pw-border p-4">
              <legend className="px-2 text-sm font-medium text-pw-text">
                {t('billing.addressLegend')}
              </legend>

              <FormField>
                <FormLabel htmlFor="line1">{t('billing.addressLine1')}</FormLabel>
                <FormControl>
                  <Input
                    id="line1"
                    value={form.billingAddress.line1}
                    onChange={(e) => setAddr('line1', e.target.value)}
                    autoComplete="address-line1"
                  />
                </FormControl>
              </FormField>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField>
                  <FormLabel htmlFor="city">{t('billing.city')}</FormLabel>
                  <FormControl>
                    <Input
                      id="city"
                      value={form.billingAddress.city}
                      onChange={(e) => setAddr('city', e.target.value)}
                      autoComplete="address-level2"
                    />
                  </FormControl>
                </FormField>
                <FormField>
                  <FormLabel htmlFor="postalCode">{t('billing.postalCode')}</FormLabel>
                  <FormControl>
                    <Input
                      id="postalCode"
                      value={form.billingAddress.postalCode}
                      onChange={(e) => setAddr('postalCode', e.target.value)}
                      autoComplete="postal-code"
                    />
                  </FormControl>
                </FormField>
              </div>

              <FormField>
                <FormLabel htmlFor="country">{t('billing.country')}</FormLabel>
                <FormControl>
                  <Select
                    value={form.billingAddress.country}
                    onValueChange={(value) => setAddr('country', value)}
                  >
                    <SelectTrigger id="country">
                      <SelectValue placeholder={t('billing.country')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="VN">{t('warehouses.countryVN')}</SelectItem>
                      <SelectItem value="US">{t('warehouses.countryUS')}</SelectItem>
                      <SelectItem value="SG">{t('warehouses.countrySG')}</SelectItem>
                      <SelectItem value="TH">{t('warehouses.countryTH')}</SelectItem>
                      <SelectItem value="JP">{t('warehouses.countryJP')}</SelectItem>
                      <SelectItem value="GB">{t('warehouses.countryGB')}</SelectItem>
                      <SelectItem value="FR">{t('warehouses.countryFR')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormField>
            </fieldset>

            {error ? (
              <FormMessage data-testid="billing-error">{error}</FormMessage>
            ) : null}
            {success ? (
              <p
                className="text-xs font-medium text-green-600"
                data-testid="billing-success"
              >
                {t('billing.success')}
              </p>
            ) : null}

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={saving} data-testid="billing-submit">
                {saving ? t('billing.submitting') : t('billing.submit')}
              </Button>
            </div>
          </Form>
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
